import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Mail, Send, Sparkles, Users, CheckCircle, Loader2, ArrowRight, ExternalLink, Globe,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { outreachApi, sourcesApi, dashboardApi, jobsApi, qualificationApi, listsApi } from '@/services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface Source {
  id: string
  name: string
  scraper_status?: string
}

interface JobStatus {
  job_id: string
  status: string
  result_summary?: {
    source?: string
    companies_processed?: number
    outreach_generated?: number
    skipped_no_contact?: number
    emails_processed?: number
    emails_sent?: number
    errors?: number
    // Enrichment fields
    total?: number
    enriched?: number
    not_found?: number
    already_has_website?: number
  } | null
  error?: { message: string } | null
}

type JobType = 'generate' | 'send' | 'enrich'

interface DashboardSummary {
  qualified: number
  totals?: {
    outreach?: number
    pending_outreach?: number
    sent?: number
  }
}

export default function OutreachPage() {
  const [selectedSource, setSelectedSource] = useState<string>('all')
  const [selectedRunId, setSelectedRunId] = useState<string>('all')
  const [limit, setLimit] = useState<number>(50)
  const [activeJob, setActiveJob] = useState<{ id: string; type: JobType } | null>(null)
  const [jobResult, setJobResult] = useState<{ type: JobType; status: JobStatus } | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data: sourcesData } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: () => sourcesApi.list()
  })

  const { data: listRunsData } = useQuery({
    queryKey: ['lists', 'runs'],
    queryFn: () => listsApi.listRuns({ limit: 50 })
  })

  const { data: summary, refetch: refetchSummary } = useQuery<DashboardSummary>({
    queryKey: ['dashboard-summary'],
    queryFn: dashboardApi.getSummary
  })

  // Poll for job status when a job is active
  useEffect(() => {
    if (!activeJob) return

    const poll = async () => {
      try {
        const status: JobStatus = await jobsApi.get(activeJob.id)
        if (status.status === 'completed' || status.status === 'failed') {
          setJobResult({ type: activeJob.type, status })
          setActiveJob(null)
          refetchSummary()
          if (pollRef.current) clearInterval(pollRef.current)
        }
      } catch {
        // Job status endpoint may not work without a real jobs table; stop polling after a timeout
      }
    }

    pollRef.current = setInterval(poll, 3000)
    // Initial check
    poll()

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [activeJob, refetchSummary])

  // Auto-clear polling after 2 minutes (fallback timeout)
  useEffect(() => {
    if (!activeJob) return
    const timeout = setTimeout(() => {
      setActiveJob(null)
      if (pollRef.current) clearInterval(pollRef.current)
    }, 120_000)
    return () => clearTimeout(timeout)
  }, [activeJob])

  const generateMutation = useMutation({
    mutationFn: () =>
      outreachApi.generate(
        selectedSource === 'all' ? undefined : selectedSource,
        limit,
        selectedRunId === 'all' ? undefined : selectedRunId
      ),
    onSuccess: (data) => {
      setJobResult(null)
      setActiveJob({ id: data.job_id, type: 'generate' })
    }
  })

  const sendMutation = useMutation({
    mutationFn: () =>
      outreachApi.send(
        selectedSource === 'all' ? undefined : selectedSource,
        limit,
        selectedRunId === 'all' ? undefined : selectedRunId
      ),
    onSuccess: (data) => {
      setJobResult(null)
      setActiveJob({ id: data.job_id, type: 'send' })
    }
  })

  const enrichMutation = useMutation({
    mutationFn: () =>
      qualificationApi.enrichWebPresence(
        selectedSource === 'all' ? undefined : selectedSource,
        limit,
        selectedRunId === 'all' ? undefined : selectedRunId
      ),
    onSuccess: (data) => {
      setJobResult(null)
      setActiveJob({ id: data.job_id, type: 'enrich' })
    }
  })

  // Only show sources with "ready" status in the dropdown
  const sources = (sourcesData || []).filter(
    (source) => source.scraper_status === 'ready'
  )
  const qualifiedCount = summary?.qualified || 0
  const readyToGenerateCount = summary?.totals?.pending_outreach ?? 0
  const outreachGeneratedCount = summary?.totals?.outreach ?? 0
  const sentCount = summary?.totals?.sent ?? 0
  const outreachReadyCount = Math.max(outreachGeneratedCount - sentCount, 0)

  const statCards = [
    {
      name: 'Qualified Companies',
      value: qualifiedCount.toLocaleString(),
      icon: Users,
      iconBg: 'bg-green-500/10',
      iconColor: 'text-green-500'
    },
    {
      name: 'Ready to Generate',
      value: readyToGenerateCount.toLocaleString(),
      icon: Sparkles,
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-500'
    },
    {
      name: 'Outreach Ready',
      value: outreachReadyCount.toLocaleString(),
      icon: Send,
      iconBg: 'bg-amber-500/10',
      iconColor: 'text-amber-500'
    },
    {
      name: 'Sent',
      value: sentCount.toLocaleString(),
      icon: Mail,
      iconBg: 'bg-purple-500/10',
      iconColor: 'text-purple-500'
    }
  ]

  const isGenerating = generateMutation.isPending || (activeJob?.type === 'generate')
  const isSending = sendMutation.isPending || (activeJob?.type === 'send')
  const isEnriching = enrichMutation.isPending || (activeJob?.type === 'enrich')
  const isAnyJobRunning = isGenerating || isSending || isEnriching

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Outreach</h1>
        <p className="mt-1 text-muted-foreground">
          Generate and send personalized outreach to qualified companies
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.name}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={cn("p-3 rounded-lg", stat.iconBg)}>
                  <stat.icon className={cn("h-6 w-6", stat.iconColor)} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.name}</p>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Outreach Controls</CardTitle>
          <CardDescription>Configure and run outreach campaigns</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Source Filter (Optional)</Label>
              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger>
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sources.map((source) => (
                    <SelectItem key={source.id} value={source.name}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>List Run (Optional)</Label>
              <Select
                value={selectedRunId}
                onValueChange={setSelectedRunId}
                disabled={!listRunsData?.length}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All runs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All runs</SelectItem>
                  {(listRunsData || []).map((run) => (
                    <SelectItem key={run.id} value={run.id}>
                      <span className="truncate max-w-[200px] block" title={run.raw_query}>
                        {run.raw_query || run.id}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Batch Limit</Label>
              <Input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                min={1}
                max={500}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => enrichMutation.mutate()}
              disabled={isAnyJobRunning}
            >
              {isEnriching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Globe className="mr-2 h-4 w-4" />
              )}
              {isEnriching ? 'Enriching...' : 'Enrich Web Presence'}
            </Button>

            <Button
              onClick={() => generateMutation.mutate()}
              disabled={isAnyJobRunning}
            >
              {isGenerating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {isGenerating ? 'Generating...' : 'Generate Outreach'}
            </Button>
            
            <Button
              variant="secondary"
              onClick={() => sendMutation.mutate()}
              disabled={isAnyJobRunning}
            >
              {isSending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {isSending ? 'Sending...' : 'Send Emails'}
            </Button>
          </div>

          {/* Active job status */}
          {activeJob && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                {activeJob.type === 'generate'
                  ? 'Generating outreach content... This may take a moment.'
                  : activeJob.type === 'send'
                  ? 'Sending outreach emails... This may take a moment.'
                  : 'Discovering websites for companies without web presence... This may take a while.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Job completed result */}
          {jobResult?.status.status === 'completed' && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription className="flex flex-col gap-2">
                <span className="font-medium">
                  {jobResult.type === 'generate'
                    ? 'Outreach generation completed!'
                    : jobResult.type === 'send'
                    ? 'Outreach emails sent!'
                    : 'Web presence enrichment completed!'}
                </span>
                {jobResult.status.result_summary && (
                  <span className="text-muted-foreground">
                    {jobResult.type === 'generate' ? (
                      <>
                        Processed {jobResult.status.result_summary.companies_processed ?? 0} companies,{' '}
                        generated {jobResult.status.result_summary.outreach_generated ?? 0} outreach messages
                        {(jobResult.status.result_summary.skipped_no_contact ?? 0) > 0 && (
                          <>, skipped {jobResult.status.result_summary.skipped_no_contact} (no contact email)</>
                        )}
                        {(jobResult.status.result_summary.errors ?? 0) > 0 && (
                          <>, {jobResult.status.result_summary.errors} errors</>
                        )}
                      </>
                    ) : jobResult.type === 'send' ? (
                      <>
                        Processed {jobResult.status.result_summary.emails_processed ?? 0} emails,{' '}
                        sent {jobResult.status.result_summary.emails_sent ?? 0} successfully
                        {(jobResult.status.result_summary.errors ?? 0) > 0 && (
                          <>, {jobResult.status.result_summary.errors} errors</>
                        )}
                      </>
                    ) : (
                      <>
                        Processed {jobResult.status.result_summary.total ?? 0} companies,{' '}
                        found websites for {jobResult.status.result_summary.enriched ?? 0}
                        {(jobResult.status.result_summary.not_found ?? 0) > 0 && (
                          <>, {jobResult.status.result_summary.not_found} not found</>
                        )}
                        {(jobResult.status.result_summary.errors ?? 0) > 0 && (
                          <>, {jobResult.status.result_summary.errors} errors</>
                        )}
                      </>
                    )}
                  </span>
                )}
                <Link
                  to="/companies"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mt-1"
                >
                  View companies with outreach
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {/* Job failed */}
          {jobResult?.status.status === 'failed' && (
            <Alert variant="destructive">
              <AlertDescription>
                {jobResult.type === 'generate' 
                  ? 'Outreach generation' 
                  : jobResult.type === 'send' 
                  ? 'Email sending' 
                  : 'Web presence enrichment'} failed
                {jobResult.status.error?.message && `: ${jobResult.status.error.message}`}
              </AlertDescription>
            </Alert>
          )}

          {/* Mutation-level success (before polling kicks in) */}
          {(generateMutation.isSuccess || sendMutation.isSuccess || enrichMutation.isSuccess) && !activeJob && !jobResult && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center gap-2">
                <span>
                  {generateMutation.isSuccess
                    ? 'Outreach generation job queued successfully!'
                    : sendMutation.isSuccess
                    ? 'Email sending job queued successfully!'
                    : 'Web presence enrichment job queued successfully!'}
                </span>
                <Link
                  to="/companies"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  View companies <ExternalLink className="h-3 w-3" />
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {(generateMutation.isError || sendMutation.isError) && (
            <Alert variant="destructive">
              <AlertDescription>
                {generateMutation.error?.message || sendMutation.error?.message || 'An error occurred'}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

    </div>
  )
}
